import { Card, Col, Row, Form, Button } from "antd";
import UploadPhoto from "../../../atoms/UploadPhoto";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { InputField, SelectField } from "../../../atoms/FormFields";
import { updateCompanyById } from "../ducks/services";
import { AxiosError } from "axios";
import { SuccessMessage } from "../../../atoms/Toasts/SuccessMessage";
import { ErrorMessage } from "../../../atoms/Toasts/ErrorMessage";
import { useLocation, useNavigate } from "react-router-dom";
import FullPageLayout from "../../../templates/FullPageLayout";
import { useAppDispatch, useAppSelector } from "../../../config/hooks";
import { getCompanyDetails, removeCompanyDetailsData } from "../ducks/actions";
import { onImageDelete, uploadUserImage } from "../../MyProfile/ducks/services";
import { RcFile } from "antd/es/upload";
import { AutoCompleteField } from "../../../atoms/FormFields/AutoComplete";
import { AddressComponent, Place } from "../ducks/types";
import { timezoneList } from "../../../config/utils";

interface FormValues {
  name: string;
  address: string;
  timezone: string;
  company_website: string;
  company_size: string;
}

const companySizeDropdown = [
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
  { value: "enterprise", label: "Enterprise" },
];

export default function EditCompany() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const url = useLocation();
  const id = url?.pathname.split("/")[2];
  const [checklistFile, setChecklistFile] = useState<File | null>(null);
  const [uploadModal, setUploadModal] = useState<boolean>(false);
  const companyDetailData = useAppSelector(
    (state) => state?.company?.companyDetailData
  );
  const [googleAddress, setGoogleAddress] = useState<Place | null>(null);
  const [previewImage, setPreviewImage] = useState<{
    file: string;
    imagePreviewUrl: string | null;
  }>({ file: "", imagePreviewUrl: null });
  const [modalPreviewImage, setModalPreviewImage] = useState<{
    file: string;
    imagePreviewUrl: string | null;
  }>({ file: "", imagePreviewUrl: null });

  const saveUpload = () => {
    setLoading(true);
    setPreviewImage({ ...modalPreviewImage });
    setLoading(false);
    setUploadModal(false);
  };

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isDirty },
  } = useForm<FormValues>();

  const onSubmit = (val: FormValues) => {
    setLoading(true);

    let city = "";
    let state = "";
    googleAddress?.address_components?.forEach(
      (component: AddressComponent) => {
        if (component.types.includes("locality")) {
          city = component.long_name;
        }
        if (component.types.includes("administrative_area_level_1")) {
          state = component.short_name;
        }
      }
    );

    const payload = {
      name: val?.name?.trim(),
      address: val?.address,
      city: city !== "" ? city : companyDetailData?.data?.city,
      state: state !== "" ? state : companyDetailData?.data?.state,
      timezone: val?.timezone,
      company_website: val?.company_website,
      company_size: val?.company_size,
    };

    updateCompanyById(id, payload)
      .then((res) => {
        if (checklistFile && res && id) {
          uploadUserImage({
            owner_id: id,
            image: checklistFile as File | RcFile,
            image_type: "company_image",
          })
            .then((res) => {
              if (res) {
                setLoading(false);
                SuccessMessage("Changes saved.");
                navigate("/companies");
              }
            })
            .catch((e: unknown) => {
              if (e instanceof AxiosError) {
                setLoading(false);
                ErrorMessage(
                  e?.response?.data?.error?.message ?? "Something went wrong"
                );
              } else {
                setLoading(false);
                ErrorMessage("Something went wrong");
              }
            });
        } else {
          setLoading(false);
          SuccessMessage("Changes saved.");
          navigate("/companies");
        }
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  useEffect(() => {
    if (id) {
      dispatch(getCompanyDetails(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (companyDetailData?.data) {
      setValue("name", companyDetailData?.data?.name);
      setValue("address", companyDetailData?.data?.address);
      setValue("timezone", companyDetailData?.data?.timezone);
      setValue("company_website", companyDetailData?.data?.company_website);
      setValue(
        "company_size",
        companyDetailData?.data?.company_size ??
          (undefined as unknown as string)
      );
    }
  }, [companyDetailData?.data, setValue]);

  function callMembersAPI() {
    dispatch(getCompanyDetails(id));
  }

  useEffect(() => {
    dispatch(removeCompanyDetailsData());
  }, [dispatch]);

  return (
    <FullPageLayout loading={companyDetailData?.loader || loading}>
      <Form
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault();
        }}
      >
        <Col style={{ width: "672px", paddingBottom: 72 }}>
          <div className="title">
            <h2>Edit company details</h2>
          </div>
          <Card>
            <Row gutter={20}>
              <Col span={24}>
                <UploadPhoto
                  size={86}
                  darkBg
                  saveUpload={saveUpload}
                  imageUploaded={companyDetailData?.data?.image_url}
                  loading={loading}
                  uploadModal={uploadModal}
                  setUploadModal={setUploadModal}
                  previewImage={previewImage}
                  checklistFile={checklistFile}
                  modalPreviewImage={modalPreviewImage}
                  setChecklistFile={setChecklistFile}
                  setPreviewImage={setPreviewImage}
                  setModalPreviewImage={setModalPreviewImage}
                  infoTextUploader="Customize the profile by adding a company logo."
                  onImageDelete={() => {
                    onImageDelete(
                      setLoading,
                      SuccessMessage,
                      callMembersAPI,
                      ErrorMessage,
                      companyDetailData?.data?.image_url
                        ? companyDetailData?.data?.image_id
                        : undefined
                    );
                  }}
                />
              </Col>

              <Col span={24} className="createForm">
                <h4> Company details </h4>
                <InputField
                  fieldName="name"
                  label="Company Name"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter company name",
                    autoComplete: "company_name",
                  }}
                  isRequired
                  rules={{
                    required: "Company name is required.",
                  }}
                  validate={errors.name && "error"}
                  validMessage={errors.name && errors.name.message}
                />

                <AutoCompleteField
                  fieldName="address"
                  label="Headquarters address"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Search address",
                  }}
                  onPlaceChange={(place: Place) =>
                    place?.formatted_address ? setGoogleAddress(place) : null
                  }
                  isRequired
                  rules={{
                    required: "Headquarters address is required.",
                  }}
                  validate={errors.address && "error"}
                  validMessage={errors.address && errors.address.message}
                />

                <SelectField
                  fieldName="timezone"
                  label="Headquarters time zone"
                  control={control}
                  selectOption={timezoneList}
                  iProps={{
                    placeholder: "Select time zone",
                  }}
                  isRequired
                  rules={{
                    required: "Timezone is required.",
                  }}
                  validate={errors.timezone && "error"}
                  validMessage={errors.timezone && errors.timezone.message}
                  tooltipTitle="All users will see the same time, based on this time zone, regardless of their actual location. For example, if an executive travels, they’ll still see the work schedule in this “home” time zone."
                />

                <InputField
                  fieldName="company_website"
                  label="Company website"
                  control={control}
                  iProps={{
                    placeholder: "Enter Website",
                    autoComplete: "company_website",
                  }}
                  tooltipTitle="The website domain will be suggested as the email domain for company employee accounts."
                  rules={{
                    validate: (val: string) => {
                      if (!val) return true;
                      const value = val.trim();
                      const regex =
                        /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.){1,}([a-zA-Z]{2,})(\/[a-zA-Z0-9-._~:/?#[\]@!$&'()*+,;=]*)?$/;
                      return regex.test(value) || "Enter a valid website";
                    },
                  }}
                  validate={errors.company_website && "error"}
                  validMessage={
                    errors.company_website && errors.company_website.message
                  }
                />

                <SelectField
                  fieldName="company_size"
                  label="Company size"
                  control={control}
                  selectOption={companySizeDropdown}
                  iProps={{
                    placeholder: "Select company size",
                  }}
                  className="bottom_0"
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <div className="fixed_btm_card">
          <Card>
            <div className="centered">
              <Button
                htmlType="button"
                className="outlined-btn"
                onClick={() => navigate("/companies")}
              >
                Cancel
              </Button>
              <Button
                htmlType="submit"
                type="primary"
                className="mint-btn"
                disabled={!isDirty}
              >
                Save changes
              </Button>
            </div>
          </Card>
        </div>
      </Form>
    </FullPageLayout>
  );
}
